/**
 * BudgetVerdictBar — diverging probability bar for the Budget Boost default view.
 *
 * One horizontal track encodes the full price-change distribution: drop-odds
 * fill from the left (red, darker = bigger drop), rise-odds from the right
 * (green, darker = bigger rise). The divider sits at P(drop), so the green/red
 * balance reads the lean at a glance.
 */

import { m } from "@/paraglide/messages";
import {
  type BudgetItem,
  columnPointsNeededLabel,
  resultMagnitude,
  summarizeVerdict,
} from "../lib/budget-display";

interface BudgetVerdictBarProps {
  item: BudgetItem;
  /** Bar height. Default is comfortable for cards; `sm` for dense rows. */
  size?: "sm" | "md";
  /** Show the "drop %" / "rise %" labels above the bar. */
  showLabels?: boolean;
}

export function BudgetVerdictBar({
  item,
  size = "md",
  showLabels = true,
}: BudgetVerdictBarProps) {
  const { riseProb, dropProb, segments, total } = summarizeVerdict(item);
  const barH = size === "sm" ? "h-3" : "h-4";
  const hasData = total > 0;

  // Divider position (from left) = share of probability that is a price drop.
  const dividerLeft = total > 0 ? (dropProb / total) * 100 : 50;

  return (
    <div className="w-full">
      {showLabels && (
        <div className="flex items-center justify-between text-[11px] font-medium mb-1 tabular-nums">
          {/* red-500 / green-400 keep AA contrast on the dark background at 11px */}
          <span className="text-red-500">
            {m.f1_budget_price_drop()} {Math.round(dropProb)}%
          </span>
          <span className="text-green-400">
            {Math.round(riseProb)}% {m.f1_budget_price_rise()}
          </span>
        </div>
      )}

      <div
        className={`relative w-full ${barH} rounded-full bg-muted/40 overflow-hidden flex`}
        role="img"
        aria-label={`${Math.round(dropProb)}% ${m.f1_budget_price_drop()}, ${Math.round(
          riseProb,
        )}% ${m.f1_budget_price_rise()}`}
      >
        {hasData ? (
          segments.map((seg) => {
            const ptsLabel = columnPointsNeededLabel(
              seg.col,
              item.currentPrice,
              item.pointsRMinus2,
              item.pointsRMinus1,
            );
            return (
              <div
                key={seg.col}
                className="h-full transition-[width] duration-300"
                style={{ width: `${seg.width}%`, backgroundColor: seg.color }}
                title={`${seg.col} → ${seg.probability}%${
                  ptsLabel ? ` (${ptsLabel} pts)` : ""
                }`}
              />
            );
          })
        ) : (
          <div className="m-auto text-[10px] text-muted-foreground">—</div>
        )}

        {/* Centre-of-gravity divider between drop and rise mass */}
        {hasData && (
          <div
            className="absolute top-0 bottom-0 w-px bg-foreground/40"
            style={{ left: `${dividerLeft}%` }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

interface BudgetResultBarProps {
  item: BudgetItem;
  size?: "sm" | "md";
  showLabels?: boolean;
}

/**
 * BudgetResultBar — shown once a race is finished and the price change is KNOWN.
 *
 * No probabilities: a single directional magnitude bar grows right (green) for
 * a rise or left (red) for a drop, scaled against the tier's maximum swing so
 * results are comparable across drivers.
 */
export function BudgetResultBar({
  item,
  size = "md",
  showLabels = true,
}: BudgetResultBarProps) {
  const delta = item.expectedPriceDelta;
  const { magnitude } = resultMagnitude(delta, item.tier);
  const isRise = delta > 0;
  const isDrop = delta < 0;
  const barH = size === "sm" ? "h-3" : "h-4";
  const fillPct = `${Math.round(magnitude * 100)}%`;

  return (
    <div className="w-full">
      {showLabels && (
        <div className="flex items-center justify-between text-[11px] font-medium mb-1">
          <span className="text-muted-foreground">
            {m.f1_budget_final_change()}
          </span>
          <span
            className={
              isRise
                ? "text-green-400"
                : isDrop
                  ? "text-red-500"
                  : "text-muted-foreground"
            }
          >
            {isRise
              ? m.f1_budget_price_rise()
              : isDrop
                ? m.f1_budget_price_drop()
                : m.f1_budget_no_change()}
          </span>
        </div>
      )}

      <div
        className={`relative w-full ${barH} rounded-full bg-muted/40 overflow-hidden flex`}
        role="img"
        aria-label={`${
          isRise
            ? m.f1_budget_price_rise()
            : isDrop
              ? m.f1_budget_price_drop()
              : m.f1_budget_no_change()
        } ${delta > 0 ? "+" : ""}${delta.toFixed(2)}`}
      >
        {/* Left half — drops fill toward the centre */}
        <div className="w-1/2 h-full flex justify-end">
          {isDrop && (
            <div
              className="h-full rounded-l-full"
              style={{ width: fillPct, backgroundColor: "var(--destructive)" }}
            />
          )}
        </div>
        {/* Right half — rises fill away from the centre */}
        <div className="w-1/2 h-full flex justify-start">
          {isRise && (
            <div
              className="h-full rounded-r-full"
              style={{ width: fillPct, backgroundColor: "var(--success)" }}
            />
          )}
        </div>
        {/* Centre baseline */}
        <div
          className="absolute top-0 bottom-0 left-1/2 w-px bg-foreground/40"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
